import { useState } from "react";
import type { EvalImage, ImageEvalState } from "../../types/creative-eval";
import { INPUT_IMAGE_GROUPS } from "./constants";
import ReasonButtons from "./reason-buttons";
import FailDetails from "./fail-details";
import InputImages from "./input-images";

interface ImageCardProps {
  image: EvalImage;
  evalState: ImageEvalState;
  onEvalChange: (state: ImageEvalState) => void;
  onLightbox: (url: string) => void;
}

const ImageCard: React.FC<ImageCardProps> = ({ image, evalState, onEvalChange, onLightbox }) => {
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const markFailed = (url: string) => setFailedUrls((prev) => new Set(prev).add(url));

  const handleToggleCategory = (category: string) => {
    const next = new Set(evalState.categories);
    if (next.has(category)) {
      next.delete(category);
      const newDetails = { ...evalState.details };
      delete newDetails[category];
      onEvalChange({ ...evalState, categories: next, details: newDetails });
    } else {
      next.add(category);
      onEvalChange({ ...evalState, categories: next });
    }
  };

  const inputGroups = INPUT_IMAGE_GROUPS.map((group) => ({
    label: group.label,
    images: image[group.key]
  })).filter((group) => group.images.length > 0);

  const isEvaluated = evalState.categories.size > 0;
  const actionClass = (image.image_action || "").toLowerCase();
  const isEdit = image.chain_label && image.chain_label !== "STANDALONE" && image.chain_label !== "ORIGINAL";
  const editDepth = image.edit_depth || 0;

  return (
    <div className={`image-card ${isEvaluated ? "evaluated" : ""}`}>
      <div className="card-image-col">
        {image.parent_url && (
          <div className="parent-image-wrap">
            <div className="prompt-label parent-image-label">Edited From</div>
            {failedUrls.has(image.parent_url) ? (
              <div className="card-image-fallback parent-image">Image unavailable</div>
            ) : (
              <button className="card-image-button" type="button" onClick={() => onLightbox(image.parent_url!)}>
                <img
                  className="card-image parent-image"
                  src={image.parent_url}
                  alt={`Parent #${image.parent_image_id || image.original_image_id || image.creative_id}`}
                  loading="lazy"
                  decoding="async"
                  onError={() => markFailed(image.parent_url!)}
                />
              </button>
            )}
          </div>
        )}
        {image.output_url && (
          <>
            {failedUrls.has(image.output_url) ? (
              <div className="card-image-fallback">Output image unavailable</div>
            ) : (
              <button
                className="card-image-button output-image-button"
                type="button"
                onClick={() => onLightbox(image.output_url!)}
              >
                <img
                  className="card-image"
                  src={image.output_url}
                  alt={`Output #${image.creative_id}`}
                  loading="lazy"
                  decoding="async"
                  onError={() => markFailed(image.output_url!)}
                />
              </button>
            )}
          </>
        )}
      </div>

      <div className="card-body">
        <div className="card-meta">
          <span className="meta-badge meta-id">#{image.creative_id}</span>
          <span className={`meta-badge meta-action ${actionClass}`}>{image.image_action}</span>
          {image.brand_name && <span className="meta-badge meta-brand">{image.brand_name}</span>}
          {image.model_name && <span className="meta-badge">{image.model_name}</span>}
          {image.creator_username && <span className="meta-badge">{image.creator_username}</span>}
          {isEdit && <span className="meta-badge meta-edit">{image.chain_label}</span>}
          {editDepth > 0 && (
            <span className={`meta-badge ${editDepth > 4 ? "meta-depth-warning" : ""}`}>Depth {editDepth}</span>
          )}
          {image.dimensions && <span className="meta-badge">{image.dimensions}</span>}
          {image.creation_time_pst && <span className="meta-badge">{image.creation_time_pst}</span>}
        </div>

        {image.prompt && (
          <>
            <div className="prompt-label">Prompt</div>
            <div className="prompt-text">{image.prompt}</div>
          </>
        )}

        {image.internal_prompt_injection && (
          <>
            <div className="prompt-label">Internal Injection</div>
            <div className="prompt-text injection-text">{image.internal_prompt_injection}</div>
          </>
        )}

        {inputGroups.map((group) => (
          <InputImages key={group.label} label={group.label} images={group.images} onImageClick={onLightbox} />
        ))}

        <ReasonButtons selected={evalState.categories} onToggle={handleToggleCategory} />

        {isEvaluated && (
          <FailDetails
            selectedCategories={evalState.categories}
            details={evalState.details}
            severity={evalState.severity}
            notes={evalState.notes}
            onDetailChange={(cat, detail) =>
              onEvalChange({ ...evalState, details: { ...evalState.details, [cat]: detail } })
            }
            onSeverityChange={(sev) => onEvalChange({ ...evalState, severity: sev })}
            onNotesChange={(n) => onEvalChange({ ...evalState, notes: n })}
          />
        )}
      </div>
    </div>
  );
};

export default ImageCard;
